import { useApolloClient } from "@apollo/client";
import { LoadingSpinner } from "@msys/ui";
import { Button, Stack, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../clients/graphqlTypes.js";
import { useLanguages } from "../../common/translations/useLanguages.js";
import { LoginButton } from "../auth/LoginButton.js";
import { RegisterButton } from "../auth/RegisterButton.js";
import { useAuth } from "../auth/useAuth.js";
import { useSelectedUser } from "../auth/useSelectedUser.js";
import { OrganisationAvatar } from "../features/organisations/OrganisationAvatar.js";
import { ButtonDivider } from "./ButtonDivider.js";
import { InvitationTokenData } from "./InvitationTokenData.js";
import { useAcceptOrganisationMembershipInvitationMutation } from "./OrganisationMembershipInvitation.generated.js";
import { useInvitationToken } from "./useInvitationToken.js";

interface Prefill {
  title: string;
  firstname: string;
  familyname: string;
  email: string;
}

// The token query resolves this field to either the pending invitation or
// the data-free `InvitationNoLongerOpen` member (see
// InvitationTokenProvider.gql) — never a settled invitation's detail.
type Invitation = Extract<
  InvitationTokenData,
  { __typename: "OrganisationMembershipInvitation" | "InvitationNoLongerOpen" }
>;

type OpenInvitation = Extract<
  Invitation,
  { __typename: "OrganisationMembershipInvitation" }
>;

interface Props {
  invitation: Invitation;
  isAuthenticated: boolean;
}

export const OrganisationMembershipInvitation = ({
  invitation,
  isAuthenticated,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const { currentLanguage } = useLanguages();
  const { setSelectedUserId } = useSelectedUser();
  const { remove: removeInvitationToken } = useInvitationToken();
  const { auth } = useAuth();

  const client = useApolloClient();
  const [acceptOrgmembershipInvitation] =
    useAcceptOrganisationMembershipInvitationMutation({
      client,
      refetchQueries: [namedOperations.Query.Me],
      awaitRefetchQueries: true,
    });

  // The union member, not a `status` field, is the source of truth — the
  // selection drops `status` because the backend only ever returns the
  // `OrganisationMembershipInvitation` member while it is PENDING.
  const pendingInvitation: OpenInvitation | null =
    invitation.__typename === "OrganisationMembershipInvitation"
      ? invitation
      : null;

  const authenticatedUserEmail = auth.idTokenParsed?.email;

  const authenticatedUserIsInvitee =
    pendingInvitation !== null &&
    authenticatedUserEmail !== undefined &&
    authenticatedUserEmail.toLowerCase() ===
      pendingInvitation.user.email.toLowerCase();

  const hasAccepted = React.useRef(false);

  const accept = React.useCallback(async () => {
    if (!pendingInvitation) return;
    const { data } = await acceptOrgmembershipInvitation({
      variables: {
        input: {
          token: pendingInvitation.invitationToken,
          locale: currentLanguage.locale,
        },
      },
    });
    if (!data) throw new Error("Failed to accept invitation!");
    removeInvitationToken();
    setSelectedUserId(data.acceptOrganisationMembershipInvitation.user.id);
  }, [
    acceptOrgmembershipInvitation,
    currentLanguage.locale,
    pendingInvitation,
    removeInvitationToken,
    setSelectedUserId,
  ]);

  React.useEffect(() => {
    if (
      pendingInvitation &&
      authenticatedUserIsInvitee &&
      !hasAccepted.current
    ) {
      hasAccepted.current = true;
      // React error boundaries do not catch rejected promises (see
      // docs/guides/frontend-error-boundaries.md); handle the rejection here
      // instead. No toast of our own — the Apollo error link already
      // renders the GraphQL error message for every failed operation, and a
      // second one here would report the same failure twice.
      accept().catch(error => {
        Sentry.captureException(error);
      });
    }
  }, [accept, authenticatedUserIsInvitee, pendingInvitation]);

  if (!pendingInvitation) {
    return (
      <Stack
        spacing={6}
        sx={{
          alignItems: "center",
        }}
      >
        <Typography variant="h3">
          {t("Sorry, this invitation is no longer valid!", {
            ns: "Global",
          })}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          onClick={() => {
            removeInvitationToken();
          }}
        >
          {t("Return to MeisterSystems", { ns: "Global" })}
        </Button>
      </Stack>
    );
  }

  if (!isAuthenticated) {
    return (
      <OrganisationMembershipInvitationUnAuhenticated
        invitation={pendingInvitation}
      />
    );
  }

  if (!authenticatedUserIsInvitee) {
    return (
      <OrganisationMembershipInvitationInvalidAccount
        invitation={pendingInvitation}
      />
    );
  }

  return <LoadingSpinner />;
};

const OrganisationMembershipInvitationUnAuhenticated = ({
  invitation,
}: {
  invitation: OpenInvitation;
}) => {
  const { t } = useTranslate(["Global"]);

  const prefill = React.useMemo(() => {
    return getPrefillData(invitation.user);
  }, [invitation.user]);

  return (
    <Stack
      direction="column"
      spacing={6}
      sx={{
        alignItems: "center",
      }}
    >
      <OrganisationAvatar
        organisationAvatar={invitation.organisation}
        size="xl"
      />
      <Typography variant="h3" align="center">
        {t(
          "Please sign in to join the {inviterOrgName} account on MeisterSystems",
          {
            ns: "Global",
            inviterOrgName: invitation.organisation.title,
          }
        )}
      </Typography>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          minWidth: 300,
        }}
      >
        <RegisterButton prefill={prefill} color="primary" />
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <LoginButton variant="text" color="secondary" prefill={prefill} />
      </Stack>
    </Stack>
  );
};

const OrganisationMembershipInvitationInvalidAccount = ({
  invitation,
}: {
  invitation: OpenInvitation;
}) => {
  const { auth } = useAuth();
  const { currentLanguage } = useLanguages();
  const { t } = useTranslate(["Global"]);
  const { remove: removeInvitationToken } = useInvitationToken();

  const prefill = React.useMemo(() => {
    return getPrefillData(invitation.user);
  }, [invitation.user]);

  return (
    <Stack
      spacing={6}
      sx={{
        alignItems: "center",
      }}
    >
      <OrganisationAvatar
        organisationAvatar={invitation.organisation}
        size="xl"
      />
      <Typography variant="h3" align="center">
        {t(
          "You must be logged in with {inviteeEmail} to join {inviterOrgName}'s team on MeisterSystems",
          {
            ns: "Global",
            inviterOrgName: invitation.organisation.title,
            inviteeEmail: invitation.user.email,
          }
        )}
      </Typography>
      <Stack
        direction="column"
        spacing={2}
        sx={{
          alignItems: "stretch",
          minWidth: 300,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disableElevation
          onClick={async () => {
            try {
              const url = new URL(
                await auth.createRegisterUrl({ locale: currentLanguage.locale })
              );
              if (prefill) {
                url.searchParams.set("email", prefill.email);
                url.searchParams.set("firstName", prefill.firstName);
                url.searchParams.set("lastName", prefill.lastName);
                url.searchParams.set(
                  "user.attributes.salutation",
                  prefill.salutation
                );
              }

              await auth.logout({
                redirectUri: url.toString(),
              });
            } catch (e) {
              Sentry.captureException(e);
            }
          }}
        >
          {t("Log out", { ns: "Global" })}
        </Button>
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <Button
          color="secondary"
          onClick={() => {
            removeInvitationToken();
          }}
        >
          {t("Return to MeisterSystems", { ns: "Global" })}
        </Button>
      </Stack>
    </Stack>
  );
};

function getPrefillData(contact: Prefill) {
  const firstName = contact.firstname;
  const lastName = contact.familyname;
  const email = contact.email;
  const salutation = contact.title;

  return {
    firstName,
    lastName,
    email,
    salutation,
  };
}
